import React, { ChangeEvent, useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Pagination from '@mui/material/Pagination';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { ApiError, fetchOpenDeposits } from './customerCenterApi';
import { displayMoney } from './money';
import type { DepositId, OpenDeposit, OpenDepositStatus } from './types';
import CustomerDepositDetail from './CustomerDepositDetail';

const PAGE_SIZE = 20;
const PLACEHOLDER = '--';

const STATUS_VALUES: OpenDepositStatus[] = ['all_open', 'unallocated', 'partial'];

function errorMessageFor(error: ApiError | Error, unavailable: string): string {
  if (error instanceof ApiError && error.status === 503) return unavailable;
  const text = error instanceof ApiError ? error.messageText : undefined;
  return text || error.message;
}

interface OpenDepositsPage {
  items: OpenDeposit[];
  total: number;
}

interface OpenDepositsViewProps {
  branchId: string | number;
}

function toDisplayDate(value: string | null): string {
  if (!value) return PLACEHOLDER;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : value;
}

function lastPageFor(total: number): number {
  return Math.max(Math.ceil(total / PAGE_SIZE), 1);
}

const OpenDepositsView: React.FC<OpenDepositsViewProps> = ({ branchId }) => {
  const { t } = useTranslation('scenes.customerCenter');
  const [status, setStatus] = useState<OpenDepositStatus>('all_open');
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState<OpenDepositsPage | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [openId, setOpenId] = useState<DepositId | null>(null);
  const [reloadKey, setReloadKey] = useState<number>(0);

  const filterKey = `${String(branchId)}|${status}`;
  const [appliedFilterKey, setAppliedFilterKey] = useState<string>(filterKey);
  if (filterKey !== appliedFilterKey) {
    setAppliedFilterKey(filterKey);
    setPage(1);
    setOpenId(null);
    setData(null);
    setError(null);
  }

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchOpenDeposits(branchId, status, page, PAGE_SIZE, controller.signal).then(
      (result) => {
        if (controller.signal.aborted) return;
        setData(result);
        setLoading(false);
      },
      (err: unknown) => {
        if (controller.signal.aborted) return;
        setData(null);
        setLoading(false);
        setError(err instanceof Error ? err : new Error(t('openFailed')));
      },
    );
    return () => controller.abort();
  }, [branchId, status, page, reloadKey, t]);

  const handlePageChange = (_event: ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const statusLabel = (value: OpenDepositStatus) => {
    if (value === 'unallocated') return t('openStatusUnallocated');
    if (value === 'partial') return t('openStatusPartial');
    return t('openStatusAll');
  };

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const pages = lastPageFor(total);
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="open-deposits">
      {/* fixme: placeholder copy */}
      <p className="customer-center__intro">{t('openSubtitle')}</p>
      <div className="customer-center__toolbar">
        <div className="customer-center__pills" role="group" aria-label={t('openShowLabel')}>
          {STATUS_VALUES.map((value) => (
            <Button
              key={value}
              size="sm"
              variant={status === value ? 'primary' : 'light'}
              className="rounded-pill px-3"
              aria-pressed={status === value}
              onClick={() => setStatus(value)}
            >
              {statusLabel(value)}
            </Button>
          ))}
        </div>
        {total > 0 ? <span className="customer-center__count text-muted small">{t('openShowing', { from, to, total })}</span> : null}
      </div>

      <div className={`page-loader ${loading ? 'visible' : ''}`}>
        <div className="loader" />
      </div>

      {error ? (
        <Alert variant="danger" className="d-flex align-items-center justify-content-between gap-3">
          <span>{errorMessageFor(error, t('unavailable'))}</span>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setReloadKey((k) => k + 1)}>
            {t('tryAgain')}
          </button>
        </Alert>
      ) : null}
      <div className="table-responsive list-table customer-center__stack-scroll">
        <table className="table table-default customer-center__stack-table">
          <thead>
            <tr className="align-middle">
              <th className="text-start">{t('colDate')}</th>
              <th className="text-start">{t('colDescription')}</th>
              <th className="text-end">{t('openColDepositAmount')}</th>
              <th className="text-end">{t('openColAssigned')}</th>
              <th className="text-end">{t('openColRemaining')}</th>
              <th className="text-start">{t('openColStatus')}</th>
              <th className="text-end">{t('openActions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const partly = Number(row.allocated_amount ?? 0) > 0;
              const label = row.description || toDisplayDate(row.date);
              return (
                <tr key={String(row.id)} className="align-middle h-60">
                  <td className="text-start" data-label={t('colDate')}>{toDisplayDate(row.date)}</td>
                  <td className="text-start" data-label={t('colDescription')}>
                    <div className="customer-center__desc">
                      <div>{row.description || PLACEHOLDER}</div>
                      {row.suggested_customer ? (
                        <small className="text-muted d-block">{t('openSuggested', { name: row.suggested_customer.name })}</small>
                      ) : null}
                    </div>
                  </td>
                  <td className="text-end" data-label={t('openColDepositAmount')}>{displayMoney(row.deposit_amount)}</td>
                  <td className="text-end" data-label={t('openColAssigned')}>{displayMoney(row.allocated_amount)}</td>
                  <td className="text-end fw-semibold" data-label={t('openColRemaining')}>{displayMoney(row.remaining_amount)}</td>
                  <td className="text-start" data-label={t('openColStatus')}>
                    {partly ? (
                      <Badge bg="warning" text="dark">{t('openStatusPartial')}</Badge>
                    ) : (
                      <Badge bg="light" text="dark" className="border">{t('openStatusUnallocated')}</Badge>
                    )}
                  </td>
                  <td className="text-end customer-center__open-actions" data-label={t('openAssign')}>
                    <button type="button" className="btn btn-primary btn-sm customer-center__open-assign" onClick={() => setOpenId(row.id)} aria-label={`${partly ? t('openAssignRest') : t('openAssign')}: ${label}`}>
                      {partly ? t('openAssignRest') : t('openAssign')}
                    </button>
                  </td>
                </tr>
              );
            })}
            {!loading && !error && rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center">
                  {t('openEmpty')}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="customer-center__pagination-row">
        <Pagination
          count={pages}
          page={page}
          showFirstButton
          showLastButton
          onChange={handlePageChange}
          size="medium"
          disabled={!total}
        />
      </div>

      {openId !== null ? (
        <CustomerDepositDetail
          branchId={branchId}
          depositId={openId}
          startEditing
          onClose={() => setOpenId(null)}
          onChanged={() => setReloadKey((k) => k + 1)}
        />
      ) : null}
    </div>
  );
};

export default OpenDepositsView;
